import { useState, useRef, useEffect } from "react"
import { ChevronDown, X, Check, Calendar, Ban, PackageCheck, XCircle, PhoneOff, RotateCcw } from "lucide-react"
import { BASE_URL } from "../apiConfig"
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";

export default function ChangeStatusLivreur({ fetchOrders, OrderId, OrderCode, setopenChangeStatus, onChange }) {
  const { t, locale } = useLanguage();
  const modalRef = useRef(null)
  const [showReasonForm, setShowReasonForm] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState("")
  const [selectedReason, setSelectedReason] = useState("")
  const [comment, setComment] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [selectedDate, setSelectedDate] = useState("")
  const { token } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose()
      }
    }
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose()
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [])

  const apiCall = async (payload) => {
    setLoading(true)
    setError("")
    try {
      const response = await fetch(`${BASE_URL}/api/orders-status-livreur`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()
      if (!response.ok || !data.success) {
        throw new Error(data.message || t('cors_status_livreur.update_error'))
      }
      fetchOrders()
      return data
    } catch (err) {
      setError(err.message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const handleChange = async (status) => {
    if (status === "REPORTE" || status === "ANNULE" || status === "REFUSE") {
      setSelectedStatus(status)
      setShowReasonForm(true)
      return
    }

    // Statuses that fire immediately without a reason form
    let apiStatus = "livre"
    let successMsg = t('cors_status_livreur.delivered_success')

    if (status === "PAS_DE_REPONSE") {
      apiStatus = "Pas de réponse"
      successMsg = t('cors_status_livreur.no_answer_success')
    } else if (status === "EN_LIVRAISON") {
      apiStatus = "en livraison"
      successMsg = t('cors_status_livreur.back_delivery_success')
    }

    const result = await apiCall({ order_id: OrderId, status: apiStatus })
    if (result) {
      setSuccessMessage(successMsg)
      setShowSuccess(true)
      if (onChange) onChange(OrderCode, apiStatus)

      setTimeout(() => {
        handleClose()
      }, 1500)
    }
  }

  const handleSubmitWithReason = async () => {
    let finalComment = comment;
    let finalReason = selectedReason;

    if (selectedReason === "Autre" && comment) {
      finalReason = comment;
    }

    if (selectedStatus === "REPORTE" && selectedDate) {
      const formattedDate = new Date(selectedDate).toLocaleDateString('fr-FR');
      finalComment = formattedDate;
    }

    const statusMapping = {
      REPORTE: "Reporté par livreur",
      ANNULE: "Annulé par livreur",
      REFUSE: "Refusé par livreur",
    }

    const result = await apiCall({
      order_id: OrderId,
      status: statusMapping[selectedStatus],
      raison: finalReason,
      comment: finalComment,
    })

    if (result) {
      const successActions = {
        REPORTE: t('cors_status_livreur.postponed'),
        ANNULE: t('cors_status_livreur.cancelled'),
        REFUSE: t('cors_status_livreur.refused'),
      }

      setSuccessMessage(t('cors_status_livreur.status_success', { status: successActions[selectedStatus] }))
      setShowSuccess(true)
      if (onChange) onChange(OrderCode, statusMapping[selectedStatus], { reason: finalReason, comment: finalComment })

      setTimeout(() => {
        handleClose()
      }, 1500)
    }
  }

  const handleClose = () => {
    setShowReasonForm(false)
    setSelectedStatus("")
    setSelectedReason("")
    setComment("")
    setShowSuccess(false)
    setSuccessMessage("")
    setSelectedDate("")
    setopenChangeStatus(false)
  }

  const reasonsReporte = [
    { value: "Pas de réponse", label: "reason_no_answer" },
    { value: "Injoignable", label: "reason_unreachable" },
    { value: "En voyage", label: "reason_traveling" },
    { value: "Hors zone", label: "reason_out_of_zone" },
    { value: "Autre", label: "reason_other" },
  ]

  const reasonsRefuse = [
    { value: "Client refuse le colis", label: "reason_client_refused_package" },
    { value: "Prix incorrect", label: "reason_wrong_price" },
    { value: "Produit incorrect", label: "reason_wrong_product" },
    { value: "Autre", label: "reason_other" },
  ]

  const reasonsAnnule = [
    { value: "Pas intéressé", label: "reason_not_interested" },
    { value: "Double commande", label: "reason_duplicate_order" },
    { value: "Numéro erroné", label: "reason_wrong_number" },
    { value: "Autre", label: "reason_other" },
  ]

  const reasonOptions = selectedStatus === "REPORTE"
    ? reasonsReporte
    : selectedStatus === "REFUSE"
      ? reasonsRefuse
      : reasonsAnnule

  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-[99] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200"
        >
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold dark:text-white text-slate-800 mb-2">{t('cors_status_livreur.perfect')}</h3>
            <p className="text-sm text-slate-600 dark:text-gray-300">{successMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed text-left inset-0 z-[99] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200"
      >
        <div className="relative px-6 py-5 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 rounded-t-2xl">
          <button
            onClick={handleClose}
            className="absolute cursor-pointer right-4 top-4 p-1 rounded-full hover:bg-slate-200 transition-colors duration-200"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>

          <div className="pr-8">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{t('cors_status_livreur.change_status')}</h3>
            <p className="text-sm text-slate-600 dark:text-gray-300 mt-1">{t('cors_status_livreur.order')} #{OrderCode}</p>
          </div>
        </div>

        {error && <div className="px-6 py-2 text-sm text-red-600 bg-red-50 border-b border-red-200">{error}</div>}

        {!showReasonForm ? (
          <div className="p-6 space-y-3">
            <button
              disabled={loading}
              onClick={() => handleChange("livre")}
              className="flex cursor-pointer items-center gap-4 w-full px-5 py-4 bg-white hover:bg-green-50 border border-slate-200 hover:border-green-200 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 group-hover:bg-green-200 rounded-full flex items-center justify-center">
                <PackageCheck className="w-5 h-5 text-green-600" />
              </div>
              <div className={`text-left ${locale === 'ar' ? 'text-right' : ''}`}>
                <span className="font-medium text-slate-800 dark:text-white group-hover:text-green-800">{t('cors_status_livreur.delivered')}</span>
                <p className="text-xs text-slate-500 dark:text-gray-300 mt-0.5">{t('cors_status_livreur.mark_delivered')}</p>
              </div>
            </button>

            <button
              disabled={loading}
              onClick={() => handleChange("REPORTE")}
              className="flex cursor-pointer items-center gap-4 w-full px-5 py-4 bg-white hover:bg-amber-50 border border-slate-200 hover:border-amber-200 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 group-hover:bg-amber-200 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div className={`text-left ${locale === 'ar' ? 'text-right' : ''}`}>
                <span className="font-medium text-slate-800 dark:text-white group-hover:text-amber-800">{t('cors_status_livreur.postponed')}</span>
                <p className="text-xs text-slate-500 dark:text-gray-300 mt-0.5">{t('cors_status_livreur.postpone_desc')}</p>
              </div>
            </button>

            <button
              disabled={loading}
              onClick={() => handleChange("REFUSE")}
              className="flex cursor-pointer items-center gap-4 w-full px-5 py-4 bg-white hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 group-hover:bg-red-200 rounded-full flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className={`text-left ${locale === 'ar' ? 'text-right' : ''}`}>
                <span className="font-medium text-slate-800 dark:text-white group-hover:text-red-800">{t('cors_status_livreur.refused')}</span>
                <p className="text-xs text-slate-500 dark:text-gray-300 mt-0.5">{t('cors_status_livreur.refused_desc')}</p>
              </div>
            </button>

            <button
              disabled={loading}
              onClick={() => handleChange("ANNULE")}
              className="flex cursor-pointer items-center gap-4 w-full px-5 py-4 bg-white hover:bg-purple-50 border border-slate-200 hover:border-purple-200 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 group-hover:bg-purple-200 rounded-full flex items-center justify-center">
                <Ban className="w-5 h-5 text-purple-600" />
              </div>
              <div className={`text-left ${locale === 'ar' ? 'text-right' : ''}`}>
                <span className="font-medium text-slate-800 dark:text-white group-hover:text-purple-800">{t('cors_status_livreur.cancelled')}</span>
                <p className="text-xs text-slate-500 dark:text-gray-300 mt-0.5">{t('cors_status_livreur.cancel_desc')}</p>
              </div>
            </button>

            <button
              disabled={loading}
              onClick={() => handleChange("PAS_DE_REPONSE")}
              className="flex cursor-pointer items-center gap-4 w-full px-5 py-4 bg-white hover:bg-orange-50 border border-slate-200 hover:border-orange-200 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 group-hover:bg-orange-200 rounded-full flex items-center justify-center">
                <PhoneOff className="w-5 h-5 text-orange-600" />
              </div>
              <div className={`text-left ${locale === 'ar' ? 'text-right' : ''}`}>
                <span className="font-medium text-slate-800 dark:text-white group-hover:text-orange-800">{t('cors_status_livreur.no_answer')}</span>
                <p className="text-xs text-slate-500 dark:text-gray-300 mt-0.5">{t('cors_status_livreur.no_answer_desc')}</p>
              </div>
            </button>

            <button
              disabled={loading}
              onClick={() => handleChange("EN_LIVRAISON")}
              className="flex cursor-pointer items-center gap-4 w-full px-5 py-4 bg-white hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 rounded-xl transition-all duration-200 group shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 group-hover:bg-indigo-200 rounded-full flex items-center justify-center">
                <RotateCcw className="w-5 h-5 text-indigo-600" />
              </div>
              <div className={`text-left ${locale === 'ar' ? 'text-right' : ''}`}>
                <span className="font-medium text-slate-800 dark:text-white group-hover:text-indigo-800">{t('cors_status_livreur.back_delivery')}</span>
                <p className="text-xs text-slate-500 dark:text-gray-300 mt-0.5">{t('cors_status_livreur.back_delivery_desc')}</p>
              </div>
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-sm dark:text-white font-medium text-slate-700 mb-3">
                {selectedStatus === "REPORTE" ? t('cors_status_livreur.postpone_date') : t('cors_status_livreur.custom_reason')} <span className="text-red-500">*</span>
              </label>

              {selectedStatus === "REPORTE" ? (
                <div className="space-y-4">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-4 py-3 dark:bg-stone-900 dark:text-white text-xs outline-none cursor-pointer border border-[var(--color-primary-500)] rounded-xl focus:ring-2 focus:ring-[var(--color-primary-50)] focus:border-[var(--color-primary-50)] appearance-none bg-white shadow-sm"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />

                  <div>
                    <label className="block text-sm dark:text-white font-medium text-slate-700 mb-3">
                      {t('cors_status_livreur.postpone_reason')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={selectedReason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        className="w-full px-4 py-3 dark:bg-stone-900 dark:text-white text-xs outline-none cursor-pointer border border-[var(--color-primary-500)] rounded-xl focus:ring-2 focus:ring-[var(--color-primary-50)] focus:border-[var(--color-primary-50)] appearance-none bg-white shadow-sm"
                        required
                      >
                        <option value="">{t('cors_status_livreur.select_reason')}</option>
                        {reasonsReporte.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {t(`cors_status_livreur.${reason.label}`)}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute dark:text-white right-4 top-3.5 w-4 h-4 pointer-events-none" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={selectedReason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-full px-4 py-3 dark:bg-stone-900 dark:text-white text-xs outline-none cursor-pointer border border-[var(--color-primary-500)] rounded-xl focus:ring-2 focus:ring-[var(--color-primary-50)] focus:border-[var(--color-primary-50)] appearance-none bg-white shadow-sm"
                    required
                  >
                    <option value="">{t('cors_status_livreur.select_reason')}</option>
                    {reasonOptions.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {t(`cors_status_livreur.${reason.label}`)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-3.5 w-4 h-4 pointer-events-none" />
                </div>
              )}
            </div>

            {selectedReason === "Autre" && (
              <div>
                  <label className="block text-sm dark:text-white font-medium text-slate-700 mb-3">
                    {t('cors_status_livreur.custom_reason')} <span className="text-red-500">*</span>
                  </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('cors_status_livreur.describe_reason')}
                  className="w-full dark:bg-stone-900 dark:text-white px-4 py-3 text-xs outline-none border border-[var(--color-primary-500)] rounded-xl focus:ring-2 focus:ring-[var(--color-primary-50)] focus:border-[var(--color-primary-50)] resize-none shadow-sm"
                  rows={4}
                  required
                />
              </div>
            )}

            {(selectedStatus === "ANNULE" || selectedStatus === "REFUSE") && selectedReason && selectedReason !== "Autre" && (
              <div>
                  <label className="block text-sm dark:text-white font-medium text-slate-700 mb-3">
                    {t('cors_status_livreur.comment')} <span className="text-slate-400">({t('cors_status_livreur.optional')})</span>
                  </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('cors_status_livreur.add_details')}
                  className="w-full dark:bg-stone-900 dark:text-white px-4 py-3 text-xs outline-none border border-[var(--color-primary-500)] rounded-xl focus:ring-2 focus:ring-[var(--color-primary-50)] focus:border-[var(--color-primary-50)] resize-none shadow-sm"
                  rows={4}
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowReasonForm(false)
                  setSelectedStatus("")
                  setSelectedReason("")
                  setSelectedDate("")
                  setComment("")
                }}
                className="flex-1 px-5 py-3 cursor-pointer text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl"
              >
                {t('cors_status_livreur.back')}
              </button>
              <button
                onClick={handleSubmitWithReason}
                disabled={
                  loading ||
                  !selectedReason ||
                  (selectedStatus === "REPORTE" && !selectedDate) ||
                  (selectedReason === "Autre" && !comment)
                }
                className="flex-1 px-5 py-3 cursor-pointer text-xs font-medium text-white bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] disabled:bg-[var(--color-primary-50)] disabled:cursor-not-allowed rounded-xl shadow-sm"
              >
                {loading ? t('cors_status_livreur.loading') : t('cors_status_livreur.confirm')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
